// productRouting.ts

import { Channel, Platform, ProductLike } from './taskTypes'

const str = (v?: string | null) => (v ?? '').trim().toLowerCase()

// Helpful name matchers
const nameHas = (p: ProductLike, ...needles: string[]) => {
  const name = str(p.title) || str(p.productName)
  return needles.some(n => name.includes(n.toLowerCase()))
}

// Canonical mappings by category/subcategory (fallbacks)
const CATEGORY_CHANNEL_MAP: Record<string, Channel> = {
  digital: 'Digital',
  seo: 'SEO',
  website: 'Website',
  creative: 'Creative',
  integration: 'Integration',
}

const SUBCATEGORY_CHANNEL_MAP: Record<string, Channel> = {
  search: 'Search',
  social: 'Social',
  display: 'Display',
  video: 'Video',
  ecommerce: 'Website',
  hosting: 'Website',
  'management fee': 'Digital',
  'management fee/retainer': 'Digital',
  retainer: 'Digital',
  'website build': 'Website',
  smm: 'Social',
  'graphic design': 'Creative',
  implementation: 'Integration',
  organic: 'Social', // or 'SEO' if you prefer; adjust to taste
  'organic social': 'Social',
  discovery: 'Digital',
  audio: 'Video', // or custom “Audio”, if you add a new channel
}

// Name-specific platform hints
const NAME_PLATFORM_RULES: Array<{
  when: (p: ProductLike) => boolean
  platform: Platform
}> = [
  { when: p => nameHas(p, 'meta'), platform: 'Meta' },
  { when: p => nameHas(p, 'facebook', 'instagram'), platform: 'Meta' },
  { when: p => nameHas(p, 'linkedin'), platform: 'LinkedIn' },
  { when: p => nameHas(p, 'youtube'), platform: 'YouTube' },
  { when: p => nameHas(p, 'google', 'search', 'sem', 'gads'), platform: 'Google' },
  { when: p => nameHas(p, 'paid', 'search', 'sem', 'gads'), platform: 'Google' },
  { when: p => nameHas(p, 'microsoft'), platform: 'Microsoft' },
  { when: p => nameHas(p, 'simplifi'), platform: 'Simplifi' },
  { when: p => nameHas(p, 'gtm'), platform: 'GTM' },
  { when: p => nameHas(p, 'ga4'), platform: 'GA4' },
  { when: p => nameHas(p, 'website'), platform: 'Website' },
]

const PLATFORM_FALLBACK_BY_CHANNEL: Partial<Record<Channel, Platform>> = {
  Search: 'Google',
  Display: 'Google',
  Video: 'YouTube',
  Social: 'Meta',
  SEO: 'Google',
  Integration: 'GTM',
  Website: 'Website',
  Digital: 'Google',
  Creative: 'Other',
}

export function routeProductToPlatformChannel(product: ProductLike): { platform: Platform; channel: Channel } {
  const cat = str(product.category)
  const sub = str(product.subcategory)

  // 1) Try name-based platform first
  const nameRule = NAME_PLATFORM_RULES.find(r => r.when(product))
  let platform = (nameRule?.platform ?? 'Other') as Platform

  // 2) Resolve channel: prefer subcategory mapping; fallback to category mapping
  let channel: Channel | undefined = SUBCATEGORY_CHANNEL_MAP[sub] ?? CATEGORY_CHANNEL_MAP[cat]

  // Special: if it’s “Meta Advertising” (your example) → Platform Meta, Channel Digital
  if (nameHas(product, 'meta') && (cat === 'digital' || sub === 'social')) {
    platform = 'Meta'
    channel = 'Social'
  }
  if (nameHas(product, 'seo') && (cat === 'seo' || sub === 'organic')) {
    platform = 'SEO'
    channel = 'SEO'
  }
  // Special: "Paid Search Advertising" → Google + Search
  if (nameHas(product, 'paid search')) {
    platform = 'Google'
    channel = 'Search'
  }
  // 3) If no channel yet, infer by platform kind (rough fallback)
  if (!channel) {
    switch (platform) {
      case 'Google':
      case 'Microsoft':
        channel = 'Search'
        break
      case 'Meta':
      case 'LinkedIn':
        channel = 'Social'
        break
      case 'YouTube':
        channel = 'Video'
        break
      case 'GTM':
      case 'GA4':
        channel = 'Integration'
        break
      case 'Website':
        channel = 'Website'
        break
      case 'Simplifi':
        channel = 'Display'
        break
      default:
        channel = 'Digital'
    }
  }

  // 4) Improve platform via channel fallback, if still 'Other'
  if (platform === 'Other' && PLATFORM_FALLBACK_BY_CHANNEL[channel]) {
    platform = PLATFORM_FALLBACK_BY_CHANNEL[channel]!
  }

  return { platform, channel }
}
